/**
 * PHASE AREAS (2026-10-30, docs/MODELER.md "PHASE AREAS"): one place that turns an
 * element's phase zones — the old straight-cut `phaseSplits` and the sketched
 * `phaseAreas` — into tagged pieces of whatever shape the caller hands in (a slab's
 * outline, one solved roof face).
 *
 * The zones are applied to the SUBJECT, never by partitioning the element's boundary
 * first: an area may run past the edge, and a roof face that runs onto the overhang is
 * still covered, because each zone is simply intersected with the subject. Later zones
 * win; the straight cuts come first, then the areas largest-first (so a smaller area
 * drawn inside a bigger one wins). Whatever no zone covers keeps the element's own tags.
 *
 * No zones = null (the caller keeps its untouched geometry, zero behavior change).
 */
import {
  flattenPathPts, resolvePhaseRegions,
  type PhaseAreaEntry, type PhaseId, type PhaseSplitEntry, type Pt2,
} from '@aj/bim-kernel'
import { clipperReady, differenceClosed, intersectClosed, signedArea } from './clipper'
import type { ElementSolid } from './categories/wall/generate3d'

export interface PhaseTags { phaseCreated?: PhaseId; phaseDemolished?: PhaseId }
export interface PhaseZone { id: string; rings: Pt2[][]; tags: PhaseTags }
export interface PhasePiece {
  tags: PhaseTags
  /** phaseOverrideKey of the tags */
  key: string
  /** true for the part no zone covers (the element's own tags) */
  base: boolean
  rings: Pt2[][]
  /** the piece's edges that run INSIDE the subject — the line between two phases */
  seams: { a: Pt2; b: Pt2 }[]
}

const AREA_EPS = 1e-4
const ON_EDGE_FT = 0.02

export function phaseTagKey(t: PhaseTags | undefined): string {
  return t ? `${t.phaseCreated ?? ''}|${t.phaseDemolished ?? ''}` : ''
}

export function hasPhaseZones(params: Record<string, unknown>): boolean {
  return !!((params.phaseSplits as unknown[] | null)?.length || (params.phaseAreas as unknown[] | null)?.length)
}

/** the zones in the order they apply (later wins). `boundaryRing` resolves the old
 *  straight cuts (they live on the element's own boundary). */
export function phaseZones(
  boundaryRing: Pt2[] | null,
  splits: PhaseSplitEntry[] | null | undefined,
  areas: PhaseAreaEntry[] | null | undefined,
  base: PhaseTags,
): PhaseZone[] {
  const out: PhaseZone[] = []
  if (splits?.length && boundaryRing && boundaryRing.length >= 3) {
    for (const r of resolvePhaseRegions(boundaryRing, splits, base).regions) {
      if (r.id === 'base') continue
      out.push({ id: r.id, rings: [r.ring], tags: { phaseCreated: r.phaseCreated, phaseDemolished: r.phaseDemolished } })
    }
  }
  const ringed = (areas ?? [])
    .map((a) => ({ a, ring: a.path?.nodes?.length >= 3 ? flattenPathPts(a.path) : [] }))
    .filter((x) => x.ring.length >= 3)
    .sort((x, y) => Math.abs(signedArea(y.ring)) - Math.abs(signedArea(x.ring)))
  for (const { a, ring } of ringed) {
    out.push({ id: a.id, rings: [ring], tags: { phaseCreated: a.phaseCreated ?? base.phaseCreated, phaseDemolished: a.phaseDemolished ?? base.phaseDemolished } })
  }
  return out
}

const segDist = (p: Pt2, a: Pt2, b: Pt2) => {
  const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy
  const t = L2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t))
}

/** the edges of `rings` that don't lie along the subject's own edges */
export function interiorEdges(rings: Pt2[][], subject: Pt2[][]): { a: Pt2; b: Pt2 }[] {
  const out: { a: Pt2; b: Pt2 }[] = []
  const onSubject = (p: Pt2) => subject.some((r) => r.some((q, i) => segDist(p, q, r[(i + 1) % r.length]) <= ON_EDGE_FT))
  for (const r of rings) {
    for (let i = 0; i < r.length; i++) {
      const a = r[i], b = r[(i + 1) % r.length]
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-6) continue
      const mid: Pt2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
      if (!onSubject(mid) || !onSubject(a) || !onSubject(b)) out.push({ a, b })
    }
  }
  return out
}

const keep = (rings: Pt2[][]) => rings.filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > AREA_EPS)

/**
 * The subject (rings, holes allowed — NonZero) cut into tagged pieces. Pieces with the
 * same tags are merged into one entry. Null when there are no zones or nothing a zone
 * covers lands on the subject.
 */
export function phasePieces(subject: Pt2[][], zones: PhaseZone[], base: PhaseTags): PhasePiece[] | null {
  if (!zones.length || !subject.length || !clipperReady()) return null
  let remaining = subject
  const byKey = new Map<string, PhasePiece>()
  for (let k = zones.length - 1; k >= 0; k--) {
    const z = zones[k]
    const inside = keep(intersectClosed(remaining, z.rings))
    if (!inside.length) continue
    remaining = keep(differenceClosed(remaining, z.rings))
    const key = phaseTagKey(z.tags)
    const cur = byKey.get(key)
    if (cur) cur.rings.push(...inside)
    else byKey.set(key, { tags: z.tags, key, base: false, rings: inside, seams: [] })
  }
  if (!byKey.size) return null
  const baseKey = phaseTagKey(base)
  const pieces = [...byKey.values()]
  if (remaining.length) {
    const same = byKey.get(baseKey)
    // an area tagged exactly like the element itself reads as part of the base
    if (same) { same.rings.push(...remaining); same.base = true }
    else pieces.push({ tags: base, key: baseKey, base: true, rings: remaining, seams: [] })
  }
  for (const p of pieces) if (!p.base) p.seams = interiorEdges(p.rings, subject)
  return pieces
}

/** the zone a point lands in (later zones win), or null for the element's own tags */
export function zoneAt(p: Pt2, zones: PhaseZone[]): PhaseZone | null {
  for (let k = zones.length - 1; k >= 0; k--) {
    if (zones[k].rings.some((r) => pointInRingXY(p, r))) return zones[k]
  }
  return null
}

function pointInRingXY(p: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** a phase segment just before the key's last segment, so suffix tests (`|roofing`,
 *  `|fascia|`, …) still read the same — the scope/member-paint precedent */
function keyWithPhase(key: string, tagKey: string): string {
  const i = key.lastIndexOf('|')
  const seg = `phaseSplit:${tagKey}`
  return i < 0 ? `${key}|${seg}` : `${key.slice(0, i)}|${seg}${key.slice(i)}`
}

const meshSplitMemo = new WeakMap<ElementSolid, { sig: string; out: ElementSolid[] }>()

/** a mesh vertex while cutting: position, normal, optional uv */
interface CV { p: [number, number, number]; n: [number, number, number]; uv?: [number, number] }
const lerpV = (a: CV, b: CV, t: number): CV => ({
  p: [a.p[0] + (b.p[0] - a.p[0]) * t, a.p[1] + (b.p[1] - a.p[1]) * t, a.p[2] + (b.p[2] - a.p[2]) * t],
  n: [a.n[0] + (b.n[0] - a.n[0]) * t, a.n[1] + (b.n[1] - a.n[1]) * t, a.n[2] + (b.n[2] - a.n[2]) * t],
  ...(a.uv && b.uv ? { uv: [a.uv[0] + (b.uv[0] - a.uv[0]) * t, a.uv[1] + (b.uv[1] - a.uv[1]) * t] as [number, number] } : {}),
})
/** a zone border segment as the vertical plane through it: side(p) = cross(d, p - a) */
interface CutLine { a: Pt2; d: Pt2; box: [number, number, number, number] }
const sideOf = (L: CutLine, x: number, y: number) => L.d[0] * (y - L.a[1]) - L.d[1] * (x - L.a[0])
const CUT_EPS = 1e-7

/** a convex polygon split by one vertical plane; pieces of fewer than 3 points are dropped */
function splitPoly(poly: CV[], L: CutLine): CV[][] {
  const s = poly.map((v) => sideOf(L, v.p[0], v.p[1]))
  if (s.every((x) => x >= -CUT_EPS) || s.every((x) => x <= CUT_EPS)) return [poly]
  const pos: CV[] = [], neg: CV[] = []
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length
    const a = poly[i], b = poly[j], sa = s[i], sb = s[j]
    if (sa >= -CUT_EPS) pos.push(a)
    if (sa <= CUT_EPS) neg.push(a)
    if ((sa > CUT_EPS && sb < -CUT_EPS) || (sa < -CUT_EPS && sb > CUT_EPS)) {
      const m = lerpV(a, b, sa / (sa - sb))
      pos.push(m); neg.push(m)
    }
  }
  return [pos, neg].filter((q) => q.length >= 3)
}

/** a line segment split at every crossing with the cut lines */
function splitSeg(a: [number, number, number], b: [number, number, number], lines: CutLine[]): [number, number, number][] {
  const ts = [0, 1]
  for (const L of lines) {
    const sa = sideOf(L, a[0], a[1]), sb = sideOf(L, b[0], b[1])
    if ((sa > CUT_EPS && sb < -CUT_EPS) || (sa < -CUT_EPS && sb > CUT_EPS)) ts.push(sa / (sa - sb))
  }
  ts.sort((x, y) => x - y)
  return ts.map((t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t] as [number, number, number])
}

/**
 * PHASE AREAS (2026-10-30, Matt on Dolle: the demolished part still showed, then "the
 * gutters and fascias need to be cropped accordingly"): a piece BUILT ON an element — a
 * roof's shingle courses and their course lines, roofing sheet, fascia, gutters — CUT at the
 * element's phase-zone borders and each part tagged like its zone. Triangles near a border
 * are cut by the vertical plane through each border segment that reaches them (so a fascia
 * board or a gutter's wall is trimmed exactly at the line, and a shingle at the cut is cut
 * in two, never stepped); every resulting piece, and every triangle away from a border, goes
 * to the zone its centre is in. Edge lines are cut the same way and assigned by midpoint. A
 * piece wholly outside every zone comes back unchanged (same object). Memoized on the piece
 * object + zones, so an unchanged roofing memo stays an unchanged chunk (the 3D PERF rule).
 */
export function splitSolidByZones(s: ElementSolid, zones: PhaseZone[], base: PhaseTags, sig: string): ElementSolid[] {
  if (!zones.length || !s.mesh || s.phaseOverride || s.paintParts?.length) return [s]
  const hit = meshSplitMemo.get(s)
  if (hit && hit.sig === sig) return hit.out
  const { positions: P, normals: N, indices: I, edges: E, uvs: U } = s.mesh
  const lines: CutLine[] = []
  for (const z of zones) for (const r of z.rings) {
    for (let i = 0; i < r.length; i++) {
      const a = r[i], b = r[(i + 1) % r.length]
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-6) continue
      lines.push({ a, d: [b[0] - a[0], b[1] - a[1]], box: [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])] })
    }
  }
  const tagOf = (x: number, y: number) => zoneAt([x, y], zones)?.tags ?? base
  const nVerts = P.length / 3
  const buckets = new Map<string, { tags: PhaseTags; pos: number[]; nrm: number[]; uv: number[]; idx: number[]; edges: number[]; remap: Int32Array }>()
  const bucket = (tags: PhaseTags) => {
    const k = phaseTagKey(tags)
    let b = buckets.get(k)
    if (!b) { b = { tags, pos: [], nrm: [], uv: [], idx: [], edges: [], remap: new Int32Array(nVerts).fill(-1) }; buckets.set(k, b) }
    return b
  }
  const vert = (i: number): CV => ({
    p: [P[i * 3], P[i * 3 + 1], P[i * 3 + 2]], n: [N[i * 3], N[i * 3 + 1], N[i * 3 + 2]],
    ...(U ? { uv: [U[i * 2], U[i * 2 + 1]] as [number, number] } : {}),
  })
  const pushNew = (b: ReturnType<typeof bucket>, v: CV) => {
    b.pos.push(...v.p); b.nrm.push(...v.n); if (U) b.uv.push(...(v.uv ?? [0, 0]))
    return b.pos.length / 3 - 1
  }
  const pushOld = (b: ReturnType<typeof bucket>, i: number) => {
    let nv = b.remap[i]
    if (nv < 0) {
      nv = b.pos.length / 3
      b.pos.push(P[i * 3], P[i * 3 + 1], P[i * 3 + 2]); b.nrm.push(N[i * 3], N[i * 3 + 1], N[i * 3 + 2])
      if (U) b.uv.push(U[i * 2], U[i * 2 + 1])
      b.remap[i] = nv
    }
    return nv
  }
  let touched = false
  // the zones' overall box: a triangle wholly outside it keeps the element's own tags
  let zx0 = Infinity, zy0 = Infinity, zx1 = -Infinity, zy1 = -Infinity
  for (const L of lines) { zx0 = Math.min(zx0, L.box[0]); zy0 = Math.min(zy0, L.box[1]); zx1 = Math.max(zx1, L.box[2]); zy1 = Math.max(zy1, L.box[3]) }
  const near: CutLine[] = []
  for (let t = 0; t + 2 < I.length; t += 3) {
    const i0 = I[t], i1 = I[t + 1], i2 = I[t + 2]
    const x0 = P[i0 * 3], x1 = P[i1 * 3], x2 = P[i2 * 3], y0 = P[i0 * 3 + 1], y1 = P[i1 * 3 + 1], y2 = P[i2 * 3 + 1]
    const bx0 = Math.min(x0, x1, x2), bx1 = Math.max(x0, x1, x2), by0 = Math.min(y0, y1, y2), by1 = Math.max(y0, y1, y2)
    near.length = 0
    if (bx1 >= zx0 && bx0 <= zx1 && by1 >= zy0 && by0 <= zy1) {
      for (const L of lines) {
        if (L.box[0] > bx1 || L.box[2] < bx0 || L.box[1] > by1 || L.box[3] < by0) continue
        // only a line the triangle actually straddles cuts it
        const s0 = sideOf(L, x0, y0), s1 = sideOf(L, x1, y1), s2 = sideOf(L, x2, y2)
        if ((s0 > CUT_EPS || s1 > CUT_EPS || s2 > CUT_EPS) && (s0 < -CUT_EPS || s1 < -CUT_EPS || s2 < -CUT_EPS)) near.push(L)
      }
    }
    if (!near.length) {
      const cx = (x0 + x1 + x2) / 3, cy = (y0 + y1 + y2) / 3
      const tags = cx < zx0 || cx > zx1 || cy < zy0 || cy > zy1 ? base : tagOf(cx, cy)
      if (tags !== base) touched = true
      const b = bucket(tags)
      b.idx.push(pushOld(b, i0), pushOld(b, i1), pushOld(b, i2))
      continue
    }
    let polys: CV[][] = [[vert(i0), vert(i1), vert(i2)]]
    for (const L of near) polys = polys.flatMap((q) => splitPoly(q, L))
    for (const q of polys) {
      const cx = q.reduce((a, v) => a + v.p[0], 0) / q.length, cy = q.reduce((a, v) => a + v.p[1], 0) / q.length
      const tags = tagOf(cx, cy)
      if (tags !== base) touched = true
      const b = bucket(tags)
      const ids = q.map((v) => pushNew(b, v))
      for (let k = 1; k + 1 < ids.length; k++) b.idx.push(ids[0], ids[k], ids[k + 1])
    }
  }
  const baseEdges = bucket(base).edges
  for (let e = 0; e + 5 < E.length; e += 6) {
    const ex0 = Math.min(E[e], E[e + 3]), ex1 = Math.max(E[e], E[e + 3]), ey0 = Math.min(E[e + 1], E[e + 4]), ey1 = Math.max(E[e + 1], E[e + 4])
    if (ex1 < zx0 || ex0 > zx1 || ey1 < zy0 || ey0 > zy1) { baseEdges.push(E[e], E[e + 1], E[e + 2], E[e + 3], E[e + 4], E[e + 5]); continue }
    const pts = splitSeg([E[e], E[e + 1], E[e + 2]], [E[e + 3], E[e + 4], E[e + 5]], lines)
    for (let k = 0; k + 1 < pts.length; k++) {
      const a = pts[k], c = pts[k + 1]
      if (Math.hypot(c[0] - a[0], c[1] - a[1], c[2] - a[2]) < 1e-9) continue
      const tags = tagOf((a[0] + c[0]) / 2, (a[1] + c[1]) / 2)
      if (tags !== base) touched = true
      bucket(tags).edges.push(a[0], a[1], a[2], c[0], c[1], c[2])
    }
  }
  const out: ElementSolid[] = !touched ? [s] : [...buckets.entries()]
    .filter(([, b]) => b.idx.length || b.edges.length)
    .map(([k, b]) => ({
      ...s,
      groupKey: keyWithPhase(s.groupKey, k),
      phaseOverride: b.tags,
      mesh: {
        positions: new Float32Array(b.pos), normals: new Float32Array(b.nrm), indices: new Uint32Array(b.idx), edges: b.edges,
        ...(U ? { uvs: new Float32Array(b.uv) } : {}),
      },
    }))
  meshSplitMemo.set(s, { sig, out })
  return out
}
